/* Grubwick admin — searchable restaurant picker (custom combobox).
 *
 * Replaces a native <select> for choosing the import target. "Create a new
 * restaurant" is a pinned action at the top — a distinct choice, not a peer in
 * the list — and the existing restaurants below are type-to-filter. Controlled:
 * `value` is 'new' or a restaurant id, changes emit through `onChange`. */
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Restaurant } from "../../../data/adminData";
import { Search, Plus, Check, Store, Chevron } from "../icons/icons";

export function RestaurantPicker({
  value,
  restaurants,
  onChange,
}: {
  value: string; // 'new' | restaurant id
  restaurants: Restaurant[];
  onChange: (v: string) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0); // highlighted row; 0 = the "new" action
  const [listMax, setListMax] = useState<number>(); // list scroller cap, sized to fit the viewport
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected =
    value === "new" ? null : (restaurants.find((r) => r.id === value) ?? null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return restaurants;
    return restaurants.filter((r) =>
      [r.name, r.hood, r.city].some((f) => f.toLowerCase().includes(q)),
    );
  }, [query, restaurants]);

  // Flat selectable rows for keyboard nav: index 0 is the "new" action, then
  // each filtered restaurant.
  const rowCount = filtered.length + 1;

  // When the popover opens: reset and focus the search. Outside taps close via
  // the backdrop below — a real element rather than a document listener, so on
  // mobile the tap is swallowed instead of falling through to the dropzone
  // underneath (which would otherwise re-open the file picker). The search isn't
  // auto-focused on touch, so the on-screen keyboard doesn't slide up over the
  // list.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const t = coarse
      ? undefined
      : setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      if (t) clearTimeout(t);
    };
  }, [open]);

  // Size the list scroller to the actually-visible room below it. A concrete
  // max-height on the scroller itself (rather than letting it flex inside a
  // capped popover) is what reliably makes it a touch-scroll container on mobile
  // Safari — the flex approach only engaged after a reflow. `visualViewport`
  // gives the real visible height, so the list fits above the browser toolbar
  // (and the keyboard, if open) and overflows-to-scroll instead of running off
  // the bottom of the screen where the backdrop's page-lock leaves it
  // unreachable. Recomputed as the viewport changes (toolbar, keyboard, rotate).
  useLayoutEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    function size() {
      const el = listRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const visibleBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
      setListMax(Math.max(140, visibleBottom - top - 12));
    }
    size();
    vv?.addEventListener("resize", size);
    vv?.addEventListener("scroll", size);
    window.addEventListener("resize", size);
    return () => {
      vv?.removeEventListener("resize", size);
      vv?.removeEventListener("scroll", size);
      window.removeEventListener("resize", size);
    };
  }, [open]);

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-idx="${active}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active]);

  function choose(v: string) {
    onChange(v);
    setOpen(false);
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, rowCount - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(active === 0 ? "new" : filtered[active - 1].id);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div className="combo" ref={rootRef}>
      <button
        type="button"
        className="combo-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {selected ? (
          <span className="ct-main">
            <Store size={15} /> {selected.name}
          </span>
        ) : (
          <span className="ct-main ct-new">
            <Plus size={15} /> New restaurant
          </span>
        )}
        <Chevron size={15} />
      </button>

      {open && (
        <>
          <div className="combo-backdrop" onClick={() => setOpen(false)} />
          <div className="combo-pop" role="dialog">
            <div className="combo-search">
              <Search size={15} />
              <input
                ref={inputRef}
                value={query}
                placeholder="Search restaurants…"
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
                onKeyDown={onKey}
              />
            </div>

            <div
              className="combo-list"
              role="listbox"
              ref={listRef}
              style={listMax ? { maxHeight: listMax } : undefined}
            >
              <button
                type="button"
                role="option"
                aria-selected={value === "new"}
                data-idx={0}
                className={
                  "combo-opt combo-new" + (active === 0 ? " active" : "")
                }
                onMouseEnter={() => setActive(0)}
                onClick={() => choose("new")}
              >
                <span className="co-ic create">
                  <Plus size={15} />
                </span>
                <span className="co-body">
                  <span className="co-name">Create a new restaurant</span>
                  <span className="co-sub">
                    Start fresh, name it in the next step
                  </span>
                </span>
                {value === "new" && (
                  <span className="co-tick">
                    <Check size={15} />
                  </span>
                )}
              </button>

              <div className="combo-div" />

              {filtered.length ? (
                filtered.map((r, i) => {
                  const idx = i + 1;
                  const sub = [r.hood, r.city].filter(Boolean).join(" · ");
                  return (
                    <button
                      type="button"
                      role="option"
                      aria-selected={value === r.id}
                      data-idx={idx}
                      key={r.id}
                      className={
                        "combo-opt" + (active === idx ? " active" : "")
                      }
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => choose(r.id)}
                    >
                      <span className="co-ic">
                        <Store size={15} />
                      </span>
                      <span className="co-body">
                        <span className="co-name">{r.name}</span>
                        {sub && <span className="co-sub">{sub}</span>}
                      </span>
                      {value === r.id && (
                        <span className="co-tick">
                          <Check size={15} />
                        </span>
                      )}
                    </button>
                  );
                })
              ) : (
                <div className="combo-empty">
                  No restaurants match “{query}”.
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
